//TODO , implement backend query to load data

import React, { useCallback, useEffect, useRef, useState } from "react";

import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TablePagination,
    Checkbox,
    FormControlLabel,
    Menu,
    MenuItem,
    IconButton,
    Paper,
    Box,
    InputAdornment,
    FormControl,
    Input,
    Typography,
    ToggleButtonGroup,
    ToggleButton,
    Tooltip,
    stickyHeader,
} from "@mui/material";

import ChecklistIcon from "@mui/icons-material/Checklist";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import MuiTableHead from "./MuiTableHead";
import {
    compactListParams,
    generateCSV,
    getStorage,
    httpRequest,
    onRequestError,
    printLog,
    setStorage,
} from "../../helper/global";
import { useNavigate } from "react-router-dom";
import { showAlert } from "../../reducers/alertSlice";
import { useDispatch } from "react-redux";
import cssStyle from "./MuiTable.module.css";

import Loader from "../Loader/Loader";
import { set } from "date-fns";
import MuiTableAction from "./MuiTableAction";

import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { LOCAL_STORAGE_KEY } from "../../helper/constant";
import { ca } from "date-fns/locale";

const SEARCH_MAX_LENGTH = 100;

//drops control and zero-width characters that come along with pasted text
const cleanSearchInput = (value) =>
    value
        // eslint-disable-next-line no-control-regex -- stripping them is the point
        .replace(/[\u0000-\u001F\u007F\u200B-\u200D\u2060\uFEFF]/g, "")
        .slice(0, SEARCH_MAX_LENGTH);

export default function MuiTable({
    theme,
    id = null,
    tableHeader = "",
    endPoint,
    onSelectRow,
    rowsPerPageArray = [10, 25, 50],
    headCells = [],
    allowSearch = true,
    defaultData = [],
    defaultSortIndex = 0,
    actions = [],
    updatedRows,
    deletedRows,
    filterUI = null,
    initFilterValues = null,
    tempExtraParams = null,
    prepareExtraParams = null,
    CACHE_KEY = null,
    exportColumns = [],
    stickyHeader = false,
    stickyHeaderOffset = 0,
}) {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [firstRender, setFirstRender] = useState(true);
    const [cache, setTableCache] = useState(null);
    const [search, setSearch] = useState("");
    const [extraParams, setExtraParams] = useState(null);
    const [loading, setLoading] = useState(false);

    const [order, setOrder] = useState(
        headCells[defaultSortIndex]["order"] ?? "asc",
    );

    const [orderBy, setOrderBy] = useState(headCells[defaultSortIndex]["id"]);

    const [selectedColumns, setSelectedColumns] = useState(
        headCells.filter((cell) => cell.visible).map((cell) => cell.id),
    );

    const [page, setPage] = useState(0);

    const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageArray[0]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [tableRows, setTableRows] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const inputRef = useRef(null);

    //read through a ref: fetchData/exportData are memoized and would otherwise
    //keep the first render's version of the parent's callback
    const prepareExtraParamsRef = useRef(prepareExtraParams);
    prepareExtraParamsRef.current = prepareExtraParams;

    //request-only shape of the filters; the cache keeps the original arrays
    //because initFilterValues restores the filter UI from them
    const requestParams = (params) => {
        if (!params.extraParams) {
            return params;
        }

        const prepare = prepareExtraParamsRef.current;
        const extra = prepare ? prepare(params.extraParams) : params.extraParams;

        return { ...params, extraParams: compactListParams(extra) };
    };

    const [tableHeight, setTableHeight] = useState(null);

    const [checkCache, setCheckCache] = useState([]);

    const [searchDebounce, setSearchDebounce] = useState(null); // add debounce state

    useEffect(() => {
        if (!CACHE_KEY) {
            return;
        }

        const tempCache = getStorage(CACHE_KEY, true) ?? {};

        if (tempCache && tempCache.search) {
            setSearch(tempCache.search);
        }

        if (tempCache && tempCache.order) {
            setOrder(tempCache.order);
        }

        if (tempCache && tempCache.sortBy) {
            setOrderBy(tempCache.sortBy);
        }

        if (tempCache && tempCache.page) {
            setPage(tempCache.page - 1);
        }

        if (tempCache && tempCache.limit) {
            setRowsPerPage(tempCache.limit);
        }

        if (tempCache && tempCache.selectedColumns) {
            setSelectedColumns(tempCache.selectedColumns);
        }

        if (initFilterValues) {
            initFilterValues(tempCache);
        }

        setTableCache(tempCache);

        setTimeout(() => {
            setFirstRender(false);
        }, 300);
    }, []);

    useEffect(() => {
        setExtraParams(tempExtraParams);
    }, [tempExtraParams]);

    useEffect(() => {
        if (stickyHeader === false) {
            return;
        }

        const totalHeight = window.innerHeight - stickyHeaderOffset - 150;

        setTableHeight(totalHeight);
    }, [stickyHeader, stickyHeaderOffset]);

    useEffect(() => {
        if (!CACHE_KEY || !cache) {
            return;
        }

        const tempCache = { ...cache };
        tempCache["selectedColumns"] = selectedColumns;

        setStorage(CACHE_KEY, JSON.stringify(tempCache));
    }, [cache, selectedColumns]);

    useEffect(() => {
        if (endPoint) {
            return;
        }

        const sortedRows = defaultData.sort((a, b) => {
            const acomp = isNaN(a[orderBy]) ? a[orderBy] : a[orderBy] * 1;
            const bcomp = isNaN(b[orderBy]) ? b[orderBy] : b[orderBy] * 1;

            if (acomp < bcomp) {
                return order === "asc" ? -1 : 1;
            }
            if (acomp > bcomp) {
                return order === "asc" ? 1 : -1;
            }
            return 0;
        });

        const paginatedRows = sortedRows.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage,
        );

        setTableRows(paginatedRows);
        setTotalRows(defaultData.length);
    }, [defaultData, page, rowsPerPage, orderBy, order, search]);

    // Debounce search input by 3 seconds
    useEffect(() => {
        const handler = setTimeout(() => {
            setSearchDebounce(search);
        }, 1500);
        return () => clearTimeout(handler);
    }, [search]);

    useEffect(() => {
        if (firstRender) {
            return;
        }

        if (
            searchDebounce === null ||
            (searchDebounce.trim().length > 0 &&
                searchDebounce.trim().length < 3)
        ) {
            return;
        }

        fetchData(
            page,
            rowsPerPage,
            orderBy,
            order,
            searchDebounce,
            extraParams,
        );
    }, [
        page,
        rowsPerPage,
        orderBy,
        order,
        searchDebounce,
        extraParams,
        firstRender,
    ]);

    // Refresh table data if any row is updated outside the table
    useEffect(() => {
        if (!updatedRows) {
            return;
        }

        if (updatedRows.length === 0) {
            return;
        }

        const temp = [...tableRows];
        let changed = false;
        updatedRows.forEach((updatedRow) => {
            const index = temp.findIndex((row) => row.id === updatedRow.id);
            if (index !== -1) {
                temp[index] = { ...temp[index], ...updatedRow };
                changed = true;
            }
        });

        if (changed) {
            setTableRows(temp);
        }
    }, [updatedRows]);

    useEffect(() => {
        if (!inputRef.current) {
            return;
        }

        inputRef.current.focus();
    }, [loading]);

    const fetchData = useCallback(
        async (page, rowsPerPage, orderBy, order, search, extraParams) => {
            if (!endPoint || !cache) {
                return;
            }

            try {
                if (loading) {
                    return;
                }

                setLoading(true);

                const params = {
                    page: page + 1,
                    limit: rowsPerPage,
                    sortBy: orderBy,
                    order: order,
                };

                params["search"] = search ?? "";

                if (extraParams) {
                    params["extraParams"] = extraParams;
                }

                if (cache) {
                    const tempCache = { ...cache };

                    const keys = Object.keys(params);
                    keys.forEach((key) => {
                        tempCache[key] = params[key];
                    });

                    setTableCache(tempCache);
                }

                // console.log(params);

                const result = await httpRequest(
                    endPoint,
                    requestParams(params),
                    "GET",
                    (error) => {
                        onRequestError(dispatch, navigate, {
                            title: error.title,
                            message: error.message,
                        });
                    },
                );

                setLoading(false);

                if (result === false) {
                    return;
                }
                printLog(result);
                const copy = [...result.payload.list];
                copy.forEach((row) => {
                    row.checked = checkCache.includes(row.id);
                });

                setTableRows(copy);
                setTotalRows(result.payload.total);
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        },
        [loading, cache],
    );

    const exportData = useCallback(
        async (orderBy, order, search, extraParams) => {
            if (!endPoint) {
                return;
            }

            try {
                if (loading) {
                    return;
                }

                setLoading(true);

                const params = {
                    sortBy: orderBy,
                    order: order,
                };

                if (search) {
                    params["search"] = search;
                }

                if (extraParams) {
                    params["extraParams"] = extraParams;
                }

                const result = await httpRequest(
                    `${endPoint}/export`,
                    requestParams(params),
                    "GET",
                    (error) => {
                        onRequestError(dispatch, navigate, {
                            title: error.title,
                            message: error.message,
                        });
                    },
                );

                setLoading(false);

                if (result === false) {
                    return;
                }

                const columns = exportColumns.map((column) => column.label);
                const data = [];
                result.payload.forEach((row, index) => {
                    const cellArr = [];
                    exportColumns.forEach((cell, i) => {
                        const objKey = cell.id;

                        let formatter = (value, row, index) => {
                            return value ?? "-";
                        };

                        if (cell.formatter) {
                            formatter = cell.formatter;
                        }

                        const value =
                            formatter(row[objKey], row, index, true) ?? "-";

                        cellArr.push(value);
                    });
                    data.push(cellArr);
                });

                generateCSV(columns, data, `export-data.csv`);
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        },
        [loading, exportColumns],
    );

    //Clears the saved search, filters, sort and page for this table and goes
    //back to the defaults. Saved values persist across logins (logout does not
    //clear them), so a bad saved filter would otherwise break the page on every
    //visit. Column choices are kept.
    const handleResetFilters = () => {
        setSearch("");
        setSearchDebounce("");
        setOrder(headCells[defaultSortIndex]["order"] ?? "asc");
        setOrderBy(headCells[defaultSortIndex]["id"]);
        setPage(0);
        setRowsPerPage(rowsPerPageArray[0]);

        if (initFilterValues) {
            initFilterValues(null);
        }

        if (CACHE_KEY) {
            setTableCache({ selectedColumns });
        }
    };

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = useCallback(
        (event) => {
            setRowsPerPage(parseInt(event.target.value));
            setPage(0);
        },
        [cache],
    );

    const handleColumnSelectionChange = (event) => {
        const { name, checked } = event.target;
        setSelectedColumns((prev) =>
            checked
                ? [...prev, name]
                : prev.filter((column) => column !== name),
        );
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleOnKeyUp = (event) => {
        const search = event.target.value;
        // Remove search length check from handleOnKeyUp, just update search state
        setSearch(event.target.value);
    };

    const handleOnSelectRow = (row) => {
        if (onSelectRow) {
            onSelectRow(row);
            return;
        }

        if (!row.endpoint) {
            return;
        }

        navigate(`/${row.endpoint}`);
    };

    const onCheckAll = (checked) => {
        const tempChecked = [];
        let temp = [...tableRows].map((row) => {
            row.checked = checked;
            if (checked) {
                tempChecked.push(row.id);
            }
            return row;
        });

        setTableRows([...temp]);
        setCheckCache(tempChecked);
    };

    const handleOnCheckbox = (event, row) => {
        const tempChecked = [...checkCache];
        row.checked = event.target.checked;
        if (event.target.checked) {
            tempChecked.push(row.id);
        } else {
            const index = tempChecked.indexOf(row.id);
            if (index > -1) {
                tempChecked.splice(index, 1);
            }
        }
        setCheckCache(tempChecked);

        setTableRows([...tableRows]);
    };

    const handleActionClick = async (action) => {
        switch (action.id) {
            case "export":
                await exportData(orderBy, order, search, extraParams);
                break;

            default:
                let selectedRows = tableRows.filter((row) => row.checked);
                await action.callback(selectedRows, checkCache);
                break;
        }
    };

    return (
        <Paper sx={{ px: 1, pt: 1, pb: 0 }}>
            <Box display={"flex"} justifyContent={"left"} alignItems={"center"}>
                <Typography variant="h6" flexGrow={1}>
                    {tableHeader}
                </Typography>

                {allowSearch && (
                    <FormControl
                        variant="standard"
                        sx={{ width: "25ch", mr: 5 }}
                    >
                        <Input
                            endAdornment={
                                <InputAdornment position="end">
                                    <SearchIcon />
                                </InputAdornment>
                            }
                            value={search}
                            aria-describedby="Search"
                            //search goes in the URL; a long or odd paste
                            //(e.g. from an email) can trip the AWS WAF
                            //query-string rules and block the page
                            inputProps={{ maxLength: SEARCH_MAX_LENGTH }}
                            onChange={(e) =>
                                setSearch(cleanSearchInput(e.target.value))
                            }
                            disabled={loading}
                            inputRef={inputRef}
                        />
                    </FormControl>
                )}

                {filterUI && (
                    <Box>
                        {/* wraps to a second line once the filters outgrow the
                            toolbar, which they do on a laptop width */}
                        <Box
                            display={"flex"}
                            alignItems={"center"}
                            flexWrap={"wrap"}
                            rowGap={1}
                        >
                            {filterUI}
                        </Box>
                    </Box>
                )}

                {CACHE_KEY && (
                    <Tooltip title="Reset filters" arrow>
                        <span>
                            <IconButton
                                onClick={handleResetFilters}
                                disabled={loading}
                                aria-label="Reset filters"
                            >
                                <RestartAltIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}

                <MuiTableAction
                    actions={actions}
                    handleActionClick={handleActionClick}
                />
                <Box>
                    <Tooltip title="Select Table Column" arrow>
                        <IconButton onClick={handleMenuOpen}>
                            <ChecklistIcon />
                        </IconButton>
                    </Tooltip>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                    >
                        {headCells.map((headCell) => (
                            <MenuItem key={`head-cell-${headCell.id}`}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={selectedColumns.includes(
                                                headCell.id,
                                            )}
                                            onChange={
                                                handleColumnSelectionChange
                                            }
                                            name={headCell.id}
                                        />
                                    }
                                    label={headCell.label}
                                />
                            </MenuItem>
                        ))}
                    </Menu>
                </Box>
            </Box>
            <Box sx={{ position: "relative" }}>
                <TableContainer
                    className={"thinScroll"}
                    sx={tableHeight ? { maxHeight: tableHeight } : {}}
                >
                    <Table stickyHeader>
                        <MuiTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            selectedColumns={selectedColumns}
                            headCells={headCells}
                            onCheckAll={onCheckAll}
                        />
                        <TableBody>
                            {tableRows.map((row, index) => (
                                <TableRow
                                    hover
                                    tabIndex={-1}
                                    key={`table-row-${index}`}
                                    sx={{ cursor: "pointer" }}
                                    onClick={(event) => {
                                        handleOnSelectRow(row);
                                    }}
                                >
                                    {headCells.map((cell, i) => {
                                        const objKey = cell.id;

                                        if (!selectedColumns.includes(objKey)) {
                                            return null;
                                        }

                                        const checkbox = cell.checkbox ?? false;

                                        let formatter = (
                                            value,
                                            row,
                                            index,
                                            key,
                                        ) => {
                                            return value;
                                        };

                                        if (cell.formatter) {
                                            formatter = cell.formatter;
                                        }

                                        const value =
                                            formatter(
                                                row[objKey],
                                                row,
                                                index,
                                                objKey,
                                            ) ?? "-";

                                        return (
                                            <TableCell
                                                key={`table-cell-${i}`}
                                                component="th"
                                                scope="row"
                                            >
                                                <Tooltip
                                                    title={
                                                        cell.tooltip
                                                            ? cell.tooltip(
                                                                  row[objKey],
                                                                  row,
                                                                  index,
                                                                  objKey,
                                                              )
                                                            : ""
                                                    }
                                                >
                                                    <Box
                                                        display={"flex"}
                                                        key={`table-cell-box-${i}`}
                                                        sx={cell.sx ?? {}}
                                                    >
                                                        {checkbox && (
                                                            <Checkbox
                                                                checked={
                                                                    row.checked ??
                                                                    false
                                                                }
                                                                inputProps={{
                                                                    "aria-label":
                                                                        "controlled",
                                                                }}
                                                                onChange={(
                                                                    event,
                                                                ) =>
                                                                    handleOnCheckbox(
                                                                        event,
                                                                        row,
                                                                    )
                                                                }
                                                                onClick={(
                                                                    event,
                                                                ) =>
                                                                    event.stopPropagation()
                                                                }
                                                            />
                                                        )}
                                                        {value}
                                                    </Box>
                                                </Tooltip>
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={rowsPerPageArray}
                    component="div"
                    count={totalRows}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />

                <Loader
                    loader={{ open: loading, message: "Getting data..." }}
                />
            </Box>
        </Paper>
    );
}
